import React, { useState, useEffect } from 'react';
import { getLenderSettings, saveLenderSettings } from '../api/lenderApi';
import { useAuth } from '../context/AuthContext';
import '../styles/FieldMappings.css';

const LenderSettings = () => {
  const { user } = useAuth();
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [expandedLenders, setExpandedLenders] = useState(new Set());
  const [showAddLenderModal, setShowAddLenderModal] = useState(false);
  const [showAddApiLenderModal, setShowAddApiLenderModal] = useState(false);
  const [newLender, setNewLender] = useState({
    lenderName: '',
    financingType: 'working-capital',
    tier: '1',
    emailToAddress: '',
    emailCcAddress: '',
    sfAccountId: '',
    emailSubject: '[{clientName}] New Submission - {businessName}',
    emailBody: 'Hello,\n\nAttached is a submission for {businessName}, which is currently seeking working capital. You\'ll find the completed application and recent bank statements included with this email.\n\nRequested Amount: {requestedAmount}\n\nPlease feel free to reach out if you have any questions or need further information.\n\nBest regards,\n{clientName} Team'
  });
  const [newApiLender, setNewApiLender] = useState({
    lenderName: '',
    stringId: '',
    financingType: 'working-capital',
    tier: '1',
    sfAccountId: ''
  });

  useEffect(() => {
    loadLenderSettings();
  }, []);

  const loadLenderSettings = async () => {
    try {
      setLoading(true);
      const settings = await getLenderSettings();
      setLenders(settings);
      setError('');
    } catch (err) {
      console.error('Error loading lender settings:', err);
      setError('Failed to load lender settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveLenderSettings(lenders);
      setSuccessMessage('Lender settings saved successfully!');
      setError('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving lender settings:', err);
      setError('Failed to save lender settings. Please try again.');
      setSuccessMessage('');
    } finally {
      setSaving(false);
    }
  };

  const updateLenderSetting = (lenderId, field, value) => {
    setLenders(prevLenders =>
      prevLenders.map(lender =>
        lender.stringId === lenderId
          ? { ...lender, [field]: value }
          : lender
      )
    );
  };

  const toggleLenderExpansion = (lenderId) => {
    setExpandedLenders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lenderId)) {
        newSet.delete(lenderId);
      } else {
        newSet.add(lenderId);
      }
      return newSet;
    });
  };

  const filteredLenders = lenders.filter(lender => {
    if (filterType === 'all') return true;
    if (filterType === 'working-capital') return lender.financingType === 'working-capital';
    if (filterType === 'equipment-financing') return lender.financingType === 'equipment-financing';
    if (filterType === 'sba') return lender.financingType === 'sba';
    if (filterType === 'real-estate') return lender.financingType === 'real-estate';
    return true;
  });

  const getStatusBadge = (lender) => {
    if (lender.emailOnly) return <span className="status-badge email-only">Email Only</span>;
    return <span className="status-badge api">API</span>;
  };

  const getActiveStatusBadge = (lender) => {
    if (lender.active) return <span className="status-badge active">Active</span>;
    return <span className="status-badge inactive">Inactive</span>;
  };

  const getTierBadge = (tier) => {
    return <span className={`tier-badge tier-${tier}`}>Tier {tier}</span>;
  };

  const handleNewLenderChange = (field, value) => {
    setNewLender(prev => ({ ...prev, [field]: value }));
  };

  const handleAddLender = async () => {
    try {
      if (!newLender.lenderName.trim()) {
        setError('Lender name is required');
        return;
      }

      if (!newLender.emailToAddress.trim()) {
        setError('Email address is required for email lenders');
        return;
      }

      setSaving(true);
      setError('');

      // Generate stringId from lender name (lowercase, remove special chars)
      const stringId = newLender.lenderName.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 20);
      
      // Check if stringId already exists
      if (lenders.some(lender => lender.stringId === stringId)) {
        setError('A lender with this name already exists');
        setSaving(false);
        return;
      }

      // Create new lender with email-only settings and required fields
      const lenderToCreate = {
        ...newLender,
        stringId,
        active: true,
        emailOnly: true,
        watermarkEnabled: false,
        contactInfoScramble: false,
        requireConfirmation: false
      };

      // Add the new lender to the existing lenders array and save all
      const updatedLenders = [...lenders, lenderToCreate];
      await saveLenderSettings(updatedLenders);
      
      // Refresh the lenders list
      await loadLenderSettings();
      
      setSuccessMessage('New lender added successfully!');
      setShowAddLenderModal(false);
      resetNewLenderForm();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error adding new lender:', err);
      setError('Failed to add new lender. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetNewLenderForm = () => {
    setNewLender({
      lenderName: '',
      financingType: 'working-capital',
      tier: '1',
      emailToAddress: '',
      emailCcAddress: '',
      sfAccountId: '',
      emailSubject: '[{clientName}] New Submission - {businessName}',
      emailBody: 'Hello,\n\nAttached is a submission for {businessName}, which is currently seeking working capital. You\'ll find the completed application and recent bank statements included with this email.\n\nRequested Amount: {requestedAmount}\n\nPlease feel free to reach out if you have any questions or need further information.\n\nBest regards,\n{clientName} Team'
    });
  };

  const closeModal = () => {
    setShowAddLenderModal(false);
    resetNewLenderForm();
    setError('');
  };

  // API Lender handlers
  const handleNewApiLenderChange = (field, value) => {
    if (field === 'lenderName') {
      // Auto-generate stringId from lender name
      const autoStringId = value.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 20);
      setNewApiLender(prev => ({ ...prev, lenderName: value, stringId: autoStringId }));
    } else {
      setNewApiLender(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddApiLender = async () => {
    try {
      if (!newApiLender.lenderName.trim()) {
        setError('Lender name is required');
        return;
      }

      if (!newApiLender.stringId.trim()) {
        setError('Lender ID is required');
        return;
      }

      // Validate stringId format
      if (!/^[a-z0-9]+$/.test(newApiLender.stringId)) {
        setError('Lender ID must contain only lowercase letters and numbers');
        return;
      }

      setSaving(true);
      setError('');

      // Check if stringId already exists
      if (lenders.some(lender => lender.stringId === newApiLender.stringId)) {
        setError('A lender with this ID already exists');
        setSaving(false);
        return;
      }

      // Create new API lender (emailOnly: false)
      const lenderToCreate = {
        lenderName: newApiLender.lenderName,
        stringId: newApiLender.stringId,
        financingType: newApiLender.financingType,
        tier: newApiLender.tier,
        sfAccountId: newApiLender.sfAccountId || '',
        active: true,
        emailOnly: false, // This is an API lender
        watermarkEnabled: false,
        contactInfoScramble: false,
        requireConfirmation: false,
        emailToAddress: '',
        emailCcAddress: '',
        emailSubject: '[{clientName}] New Submission - {businessName}',
        emailBody: ''
      };

      // Add the new lender to the existing lenders array and save all
      const updatedLenders = [...lenders, lenderToCreate];
      await saveLenderSettings(updatedLenders);
      
      // Refresh the lenders list
      await loadLenderSettings();
      
      setSuccessMessage(`API Lender "${newApiLender.lenderName}" added successfully! Make sure to create the file: /lenders/${newApiLender.stringId}.js`);
      setShowAddApiLenderModal(false);
      resetNewApiLenderForm();
      setTimeout(() => setSuccessMessage(''), 8000); // Longer timeout for API lender message
    } catch (err) {
      console.error('Error adding new API lender:', err);
      setError('Failed to add new API lender. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetNewApiLenderForm = () => {
    setNewApiLender({
      lenderName: '',
      stringId: '',
      financingType: 'working-capital',
      tier: '1',
      sfAccountId: ''
    });
  };

  const closeApiLenderModal = () => {
    setShowAddApiLenderModal(false);
    resetNewApiLenderForm();
    setError('');
  };

  if (loading) {
    return (
      <div className="field-mappings-container">
        <div className="loading-spinner">Loading lender settings...</div>
      </div>
    );
  }

  return (
    <div className="field-mappings-container lender-settings">
      <div className="header-section">
        <h2>Lender Settings</h2>
        <p className="subtitle">Configure lender availability, tiers, and email settings</p>
      </div>

      {/* Help Section */}
      <div className="help-section">
        <div className="help-item">
          <strong>Active:</strong> Controls whether the lender is available for submissions. Inactive lenders will not appear in the lender submission forms.
        </div>
        <div className="help-item">
          <strong>Email Only:</strong> Indicates whether this lender only accepts submissions via email rather than through API integration. Email-only lenders require manual processing.
        </div>
        <div className="help-item">
          <strong>Tier:</strong> Priority level for lender display (1 = highest priority, 3 = lowest priority). Lower tier numbers appear first in the submission interface.
        </div>
        <div className="help-item">
          <strong>Watermark:</strong> Adds your company logo diagonally across each page of bank statements that are submitted to lenders. This helps with branding and document tracking.
        </div>
        <div className="help-item">
          <strong>Contact Info Scramble:</strong> Protects sensitive information by modifying contact details before submitting to lenders.
        </div>
        <div className="help-item">
          <strong>Require Confirmation:</strong> When enabled, users must review and confirm that the deal meets the lender's criteria before submission. A confirmation modal will display matched, missed, and pending criteria based on the lender match settings.
        </div>
      </div>

      {/* Filter Section */}
      <div className="filter-section">
        <label>Filter Lenders:</label>
        <div className="filter-options">
          <label>
            <input
              type="radio"
              value="all"
              checked={filterType === 'all'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            All
          </label>
          <label>
            <input
              type="radio"
              value="working-capital"
              checked={filterType === 'working-capital'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            Working Capital
          </label>
          <label>
            <input
              type="radio"
              value="sba"
              checked={filterType === 'sba'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            SBA
          </label>
          <label>
            <input
              type="radio"
              value="real-estate"
              checked={filterType === 'real-estate'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            Real Estate
          </label>
          <label>
            <input
              type="radio"
              value="equipment-financing"
              checked={filterType === 'equipment-financing'}
              onChange={(e) => setFilterType(e.target.value)}
            />
            Equipment Financing
          </label>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Add Lender Buttons */}
      <div className="add-lender-section">
        <button 
          className="add-lender-btn" 
          onClick={() => setShowAddLenderModal(true)}
        >
          + Add Email Lender
        </button>
        {user?.role === 'developer' && (
          <button 
            className="add-lender-btn add-api-lender-btn" 
            onClick={() => setShowAddApiLenderModal(true)}
            style={{ marginLeft: '10px', backgroundColor: '#28a745' }}
          >
            + Add API Lender
          </button>
        )}
      </div>

      {/* Lenders List */}
      <div className="lenders-list">
        {filteredLenders.map((lender) => (
          <div key={lender.stringId} className={`lender-row ${expandedLenders.has(lender.stringId) ? 'expanded' : ''}`}>
            <div 
              className={`lender-header ${expandedLenders.has(lender.stringId) ? 'expanded' : ''}`}
              onClick={() => toggleLenderExpansion(lender.stringId)}
            >
              <div className="lender-info">
                <div className="lender-name-section">
                  <h3>{lender.lenderName}</h3>
                </div>
                <div className="lender-badges">
                  <span className="lender-id-badge">ID: {lender.uniqueLenderId}</span>
                  {lender.watermarkEnabled && <span className="feature-badge">Watermark ✓</span>}
                  {lender.contactInfoScramble && <span className="feature-badge">Scramble ✓</span>}
                  {lender.requireConfirmation && <span className="feature-badge">Confirmation ✓</span>}
                  {lender.allowAutoDeclineOverride === false && <span className="feature-badge" style={{ backgroundColor: '#dc3545', color: 'white' }}>No Override</span>}
                  {getActiveStatusBadge(lender)}
                  {getStatusBadge(lender)}
                  {getTierBadge(lender.tier)}
                  <span className="financing-type">
                    {lender.financingType === 'working-capital' ? 'Working Capital' : 
                     lender.financingType === 'equipment-financing' ? 'Equipment Financing' : 
                     lender.financingType === 'real-estate' ? 'Real Estate' : 'SBA'}
                  </span>
                </div>
              </div>
              <div className="expand-icon">
                {expandedLenders.has(lender.stringId) ? '▼' : '▶'}
              </div>
            </div>

            {expandedLenders.has(lender.stringId) && (
              <div className="lender-details">
                <div className="settings-grid">
                  {/* Basic Settings */}
                  <div className="settings-section">
                    <h4>Basic Settings</h4>
                    
                    <div className="setting-item">
                      <label>ACTIVE</label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={lender.active}
                          onChange={(e) => updateLenderSetting(lender.stringId, 'active', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <label>EMAIL ONLY</label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={lender.emailOnly}
                          onChange={(e) => updateLenderSetting(lender.stringId, 'emailOnly', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <label>TIER</label>
                      <select
                        value={lender.tier}
                        onChange={(e) => updateLenderSetting(lender.stringId, 'tier', e.target.value)}
                      >
                        <option value="1A">1A</option>
                        <option value="1">1 (Starting Point)</option>
                        <option value="1B">1B</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                      </select>
                    </div>
                  </div>

                  {/* Security Settings */}
                  <div className="settings-section">
                    <h4>Security Settings</h4>
                    
                    <div className="setting-item">
                      <label>WATERMARK</label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={lender.watermarkEnabled}
                          onChange={(e) => updateLenderSetting(lender.stringId, 'watermarkEnabled', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <label>CONTACT SCRAMBLE</label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={lender.contactInfoScramble}
                          onChange={(e) => updateLenderSetting(lender.stringId, 'contactInfoScramble', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <label>REQUIRE CONFIRMATION</label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={lender.requireConfirmation}
                          onChange={(e) => updateLenderSetting(lender.stringId, 'requireConfirmation', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>

                    <div className="setting-item">
                      <label>ALLOW AUTO-DECLINE OVERRIDE</label>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={lender.allowAutoDeclineOverride !== false}
                          onChange={(e) => updateLenderSetting(lender.stringId, 'allowAutoDeclineOverride', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>

                  {/* Email Settings */}
                  <div className="settings-section email-settings">
                    <h4>Email Settings</h4>
                    
                    <div className="setting-item">
                      <label>EMAIL TO ADDRESS</label>
                      <input
                        type="email"
                        placeholder="Optional"
                        value={lender.emailToAddress || ''}
                        onChange={(e) => updateLenderSetting(lender.stringId, 'emailToAddress', e.target.value)}
                      />
                    </div>

                    <div className="setting-item">
                      <label>EMAIL CC ADDRESS</label>
                      <input
                        type="email"
                        placeholder="Optional"
                        value={lender.emailCcAddress || ''}
                        onChange={(e) => updateLenderSetting(lender.stringId, 'emailCcAddress', e.target.value)}
                      />
                    </div>

                    <div className="setting-item">
                      <label>SALESFORCE ACCOUNT ID</label>
                      <input
                        type="text"
                        placeholder="e.g., 001Hu00003DWeNzIAL"
                        value={lender.sfAccountId || ''}
                        onChange={(e) => updateLenderSetting(lender.stringId, 'sfAccountId', e.target.value)}
                      />
                    </div>

                                         <div className="setting-item">
                       <label>EMAIL SUBJECT</label>
                       <input
                         type="text"
                         placeholder="Default: [{clientName}] New Submission - {businessName}"
                         value={lender.emailSubject || ''}
                         onChange={(e) => updateLenderSetting(lender.stringId, 'emailSubject', e.target.value)}
                       />
                     </div>

                     <div className="setting-item">
                       <label>EMAIL BODY</label>
                       <textarea
                         placeholder="Default: Hello,&#10;&#10;Attached is a submission for {businessName}, which is currently seeking working capital. You'll find the completed application and recent bank statements included with this email.&#10;&#10;Requested Amount: {requestedAmount}&#10;&#10;Please feel free to reach out if you have any questions or need further information.&#10;&#10;Best regards,&#10;{clientName} Team"
                         value={lender.emailBody || ''}
                         onChange={(e) => updateLenderSetting(lender.stringId, 'emailBody', e.target.value)}
                         rows={6}
                       />
                     </div>
                  </div>
                </div>

                <div className="lender-actions">
                  <button
                    className="save-btn"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredLenders.length === 0 && (
        <div className="no-lenders">
          <p>No lenders found for the selected filter.</p>
        </div>
      )}

      {/* Add Email Lender Modal */}
      {showAddLenderModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* <div className="modal-header">
              <h3>Add New Email Lender</h3>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div> */}
            
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-section">
                  <h4>Basic Information</h4>
                  
                  <div className="form-item">
                    <label>LENDER NAME *</label>
                    <input
                      type="text"
                      value={newLender.lenderName}
                      onChange={(e) => handleNewLenderChange('lenderName', e.target.value)}
                      placeholder="Enter lender name"
                      required
                    />
                  </div>

                  <div className="form-item">
                    <label>FINANCING TYPE</label>
                    <select
                      value={newLender.financingType}
                      onChange={(e) => handleNewLenderChange('financingType', e.target.value)}
                    >
                      <option value="working-capital">Working Capital</option>
                      <option value="sba">SBA</option>
                      <option value="real-estate">Real Estate</option>
                      <option value="equipment-financing">Equipment Financing</option>
                    </select>
                  </div>

                  <div className="form-item">
                    <label>TIER</label>
                    <select
                      value={newLender.tier}
                      onChange={(e) => handleNewLenderChange('tier', e.target.value)}
                    >
                      <option value="1A">1A</option>
                      <option value="1">1 (Starting Point)</option>
                      <option value="1B">1B</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                    </select>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Email Configuration</h4>
                  
                  <div className="form-item">
                    <label>EMAIL TO ADDRESS *</label>
                    <input
                      type="email"
                      value={newLender.emailToAddress}
                      onChange={(e) => handleNewLenderChange('emailToAddress', e.target.value)}
                      placeholder="lender@example.com"
                      required
                    />
                  </div>

                  <div className="form-item">
                    <label>EMAIL CC ADDRESS</label>
                    <input
                      type="email"
                      value={newLender.emailCcAddress}
                      onChange={(e) => handleNewLenderChange('emailCcAddress', e.target.value)}
                      placeholder="cc@example.com (optional)"
                    />
                  </div>

                  <div className="form-item">
                    <label>SALESFORCE ACCOUNT ID</label>
                    <input
                      type="text"
                      value={newLender.sfAccountId || ''}
                      onChange={(e) => handleNewLenderChange('sfAccountId', e.target.value)}
                      placeholder="e.g., 001Hu00003DWeNzIAL (optional)"
                    />
                  </div>

                  <div className="form-item">
                    <label>EMAIL SUBJECT</label>
                    <input
                      type="text"
                      value={newLender.emailSubject}
                      onChange={(e) => handleNewLenderChange('emailSubject', e.target.value)}
                      placeholder="[{clientName}] New Submission - {businessName}"
                    />
                  </div>

                  <div className="form-item">
                    <label>EMAIL BODY</label>
                    <textarea
                      value={newLender.emailBody}
                      onChange={(e) => handleNewLenderChange('emailBody', e.target.value)}
                      placeholder="Hello,&#10;&#10;Attached is a submission for {businessName}, which is currently seeking working capital. You'll find the completed application and recent bank statements included with this email.&#10;&#10;Requested Amount: {requestedAmount}&#10;&#10;Please feel free to reach out if you have any questions or need further information.&#10;&#10;Best regards,&#10;{clientName} Team"
                      rows={6}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="save-btn" 
                onClick={handleAddLender}
                disabled={saving || !newLender.lenderName.trim() || !newLender.emailToAddress.trim()}
              >
                {saving ? 'Adding...' : 'Add Lender'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add API Lender Modal (Developer Only) */}
      {showAddApiLenderModal && (
        <div className="modal-overlay" onClick={closeApiLenderModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-section">
                  <h4>API Lender Information</h4>
                  
                  <div className="api-lender-warning" style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    padding: '12px',
                    marginBottom: '16px',
                    fontSize: '13px'
                  }}>
                    <strong>⚠️ Important:</strong> API lenders require a corresponding JavaScript file in the <code>/lenders/</code> folder.
                    <br /><br />
                    The <strong>Lender ID</strong> you set below must match the filename exactly:
                    <br />
                    <code style={{ backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>
                      /lenders/{newApiLender.stringId || '{lenderId}'}.js
                    </code>
                    <br /><br />
                    The file must export a <code>submit()</code> function:
                    <pre style={{ 
                      backgroundColor: '#e9ecef', 
                      padding: '8px', 
                      borderRadius: '3px',
                      fontSize: '11px',
                      overflow: 'auto',
                      marginTop: '8px'
                    }}>
{`export const submit = async (contactData, businessData, processedFiles) => {
  // Your API integration code here
  return { submissionId, status: 'submitted', ... };
};`}
                    </pre>
                  </div>

                  <div className="form-item">
                    <label>LENDER NAME *</label>
                    <input
                      type="text"
                      value={newApiLender.lenderName}
                      onChange={(e) => handleNewApiLenderChange('lenderName', e.target.value)}
                      placeholder="e.g., IBF SBA"
                      required
                    />
                  </div>

                  <div className="form-item">
                    <label>LENDER ID * <span style={{ fontWeight: 'normal', fontSize: '11px', color: '#666' }}>(auto-generated, editable)</span></label>
                    <input
                      type="text"
                      value={newApiLender.stringId}
                      onChange={(e) => handleNewApiLenderChange('stringId', e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                      placeholder="e.g., ibfsba"
                      required
                      style={{ fontFamily: 'monospace' }}
                    />
                    {newApiLender.stringId && (
                      <small style={{ color: '#28a745', marginTop: '4px', display: 'block' }}>
                        File required: <code>/lenders/{newApiLender.stringId}.js</code>
                      </small>
                    )}
                  </div>

                  <div className="form-item">
                    <label>FINANCING TYPE</label>
                    <select
                      value={newApiLender.financingType}
                      onChange={(e) => handleNewApiLenderChange('financingType', e.target.value)}
                    >
                      <option value="working-capital">Working Capital</option>
                      <option value="sba">SBA</option>
                      <option value="real-estate">Real Estate</option>
                      <option value="equipment-financing">Equipment Financing</option>
                    </select>
                  </div>

                  <div className="form-item">
                    <label>TIER</label>
                    <select
                      value={newApiLender.tier}
                      onChange={(e) => handleNewApiLenderChange('tier', e.target.value)}
                    >
                      <option value="1A">1A</option>
                      <option value="1">1 (Starting Point)</option>
                      <option value="1B">1B</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                    </select>
                  </div>

                  <div className="form-item">
                    <label>SALESFORCE ACCOUNT ID</label>
                    <input
                      type="text"
                      value={newApiLender.sfAccountId || ''}
                      onChange={(e) => handleNewApiLenderChange('sfAccountId', e.target.value)}
                      placeholder="e.g., 001Hu00003DWeNzIAL (optional)"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-btn" 
                onClick={closeApiLenderModal}
                style={{ marginRight: '10px', backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button 
                className="save-btn" 
                onClick={handleAddApiLender}
                disabled={saving || !newApiLender.lenderName.trim() || !newApiLender.stringId.trim()}
                style={{ backgroundColor: '#28a745' }}
              >
                {saving ? 'Adding...' : 'Add API Lender'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LenderSettings; 